import React, { useCallback, useMemo } from 'react'
import { AppService } from '../../services'
import { Button, PageHeader } from '@pushly/aqe/lib/components'
import NotificationList from '../../components/notification-list/notification-list'
import { getPathEntityId } from '../../_utils/get-path-entity-id'
import { AppState } from '../../stores/app'
import { useService } from '@pushly/aqe/lib/hooks'
import { SubjectEntity } from '../../enums/ability-entity.enum'
import { AbilityAction } from '../../enums/ability-action.enum'
import { asCaslSubject, CurrentUserCan } from '../../stores/app-ability'
import SimpleTabbedView from '../../components/simple-tabbed-view/simple-tabbed-view'
import { NotificationListTypes } from '../../components/notification-list/enums'
import NotificationTemplateList from '../notifications/named-notification-template/notification-template-list'
import { FeedsDataViewWrapper } from '../../components/feeds/feeds-data-view-wrapper'
import { FEAT_FEEDS_MGMT } from '@pushly/aqe/lib/constants'

const SCHEDULE_NOTIFICATION_ACTION_LABEL = 'Schedule Notification'
const CREATE_TEMPLATE_ACTION_LABEL = 'Create Template'
const CREATE_FEED_ACTION_LABEL = 'Create Feed'

const Notifications = (props) => {
    const appState = useService(AppState)
    const appService = useService(AppService)

    const pathOrgId = getPathEntityId('organization')
    const pathDomainId = getPathEntityId('domain')

    /**
     * Ensure injected props are passed down
     * for history container access
     */
    const listProps: any = { ...props }
    let permissionEntity: SubjectEntity = SubjectEntity.NOTIFICATION
    let permissionConstraints: any = {}

    if (pathOrgId) {
        listProps.level = 'org'
        listProps.orgId = pathOrgId

        permissionEntity = SubjectEntity.ORG_NOTIFICATION
        permissionConstraints.accountId = pathOrgId
    } else {
        listProps.level = 'domain'
        listProps.domainId = pathDomainId

        permissionConstraints.domainId = pathDomainId
    }

    const titleEntity = pathOrgId ? 'Multi-Domain Notifications' : 'Notifications'

    let orgName
    if (pathOrgId && appState.currentDomain?.accountId === pathOrgId) {
        orgName = appState.currentDomain.accountName
    }

    const notificationListTabs = [
        {
            tabClassName: 'notification-list-tab',
            component: SimpleTabbedView.wrapComponentTab(NotificationList, {
                tabName: null,
                tabLabel: 'Notifications',
            }),
            props: () => {
                return {
                    ...listProps,
                    listType: NotificationListTypes.NOTIFICATION,
                    hideAutoRefreshOptions: true,
                    userId: appState.currentUser?.id,
                }
            },
        },
    ]

    if (!pathOrgId) {
        notificationListTabs.push(
            {
                tabClassName: 'draft-list-tab',
                component: SimpleTabbedView.wrapComponentTab(NotificationList, {
                    tabName: 'drafts',
                    tabLabel: 'Drafts',
                }),
                props: () => {
                    return {
                        ...listProps,
                        listType: NotificationListTypes.DRAFT,
                        hideAutoRefreshOptions: true,
                        userId: appState.currentUser?.id,
                    }
                },
            },
            {
                tabClassName: 'template-list-tab',
                component: SimpleTabbedView.wrapComponentTab(
                    NotificationTemplateList,
                    {
                        tabName: 'templates',
                        tabLabel: 'Templates',
                    },
                    [pathDomainId],
                ),
                props: () => {
                    return {
                        ...listProps,
                        hideAutoRefreshOptions: true,
                    }
                },
            },
            {
                tabClassName: 'feeds-list-tab',
                component: SimpleTabbedView.wrapComponentTab(
                    FeedsDataViewWrapper,
                    {
                        tabName: 'feeds',
                        tabLabel: 'Feeds',
                    },
                    [pathDomainId],
                ),
                props: () => {
                    return {
                        ...listProps,
                        domain: appState.currentDomain,
                        hideAutoRefreshOptions: true,
                        appState,
                    }
                },
            },
        )
    }

    const [activeTab, setActiveTab] = React.useState('notifications')

    const newNotifPath = useMemo(() => {
        return appService.routeWithin(listProps.level, '/notifications/new', true)
    }, [listProps.level, appState.currentDomain?.id])

    const newTemplatePath = useMemo(() => {
        return appService.routeWithin(listProps.level, '/notifications/templates/new', true)
    }, [listProps.level, appState.currentDomain?.id])

    const newFeedPath = useMemo(() => {
        return appService.routeWithin(listProps.level, '/notifications/feeds/new', true)
    }, [listProps.level, appState.currentDomain?.id])

    const getActionDefinition = useCallback(
        (label: string, route: string) => {
            return {
                text: label,
                onClick: () => appService.route(route),
                altHref: () => appService.routeWithinDomain(route, true),
            }
        },
        [appState.currentDomain?.id],
    )

    const { primaryActionLabel, primaryActionClickHandler, secondaryActions } = useMemo(() => {
        const domainHasFeedsMgmt = appState.currentDomain?.flags?.includes(FEAT_FEEDS_MGMT)

        const createNotifAction = getActionDefinition(SCHEDULE_NOTIFICATION_ACTION_LABEL, newNotifPath)
        const createTemplateAction = getActionDefinition(CREATE_TEMPLATE_ACTION_LABEL, newTemplatePath)
        const createFeedAction = getActionDefinition(CREATE_FEED_ACTION_LABEL, newFeedPath)

        let primaryLabel = SCHEDULE_NOTIFICATION_ACTION_LABEL
        let primaryRoute = newNotifPath

        const actions: any[] = []

        if (!pathOrgId) {
            if (!activeTab || activeTab === 'drafts') {
                actions.push(createTemplateAction)
                if (domainHasFeedsMgmt) {
                    actions.push(createFeedAction)
                }
            }
            if (activeTab === 'templates') {
                primaryLabel = CREATE_TEMPLATE_ACTION_LABEL
                primaryRoute = newTemplatePath
                actions.push(createNotifAction)
                if (domainHasFeedsMgmt) {
                    actions.push(createFeedAction)
                }
            }
            if (activeTab === 'feeds') {
                primaryLabel = CREATE_FEED_ACTION_LABEL
                primaryRoute = newFeedPath
                actions.push(createNotifAction, createTemplateAction)
            }
        }

        return {
            primaryActionLabel: primaryLabel,
            primaryActionClickHandler: () => {
                appService.route(primaryRoute)
            },
            secondaryActions: actions,
        }
    }, [
        pathDomainId,
        pathOrgId,
        activeTab,
        newNotifPath,
        newTemplatePath,
        newFeedPath,
        listProps.level,
        appState.currentDomain?.id,
        appState.currentDomain?.flags,
        getActionDefinition,
    ])

    return (
        <div className="notification-list">
            <PageHeader
                title={titleEntity}
                onTitleSet={appService.customizeTabTitle}
                append={orgName}
                action={
                    <CurrentUserCan
                        do={AbilityAction.CREATE}
                        on={asCaslSubject(permissionEntity, permissionConstraints)}
                    >
                        <Button
                            className="new-notification-button"
                            type="primary"
                            onClick={primaryActionClickHandler}
                            altHref={newNotifPath}
                            actions={secondaryActions}
                        >
                            <span>{primaryActionLabel}</span>
                        </Button>
                    </CurrentUserCan>
                }
            />

            <SimpleTabbedView
                pathStyle="slash"
                animated={false}
                tabs={notificationListTabs}
                onTabChange={(tab) => {
                    const activeTabKey = 'name' in tab ? tab.name : tab.component.tabName
                    setActiveTab(activeTabKey)
                }}
            />
        </div>
    )
}

export default Notifications
